import { SelectMenu } from '@postscript/components';
import { FieldAttributes, useField, useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import FormikField from './FormikField';

interface Option {
  label: string;
  value: string | number;
}

export interface Props extends FieldAttributes<any> {
  name: string;
  options: Option[];
  openMenuMaxVisibleOptions?: number;
}

const StyledSelectMenu = styled(SelectMenu)<{
  $maxVisibleOptions?: number;
}>`
  ${({ $maxVisibleOptions }) =>
    $maxVisibleOptions &&
    `.react-select__menu div { max-height: ${$maxVisibleOptions * 35}px; }`}
`;

function FormikFieldSelectMenu({
  name,
  options = [],
  openMenuMaxVisibleOptions,
  ...props
}: Readonly<Props>): JSX.Element {
  const { setFieldValue } = useFormikContext();
  const [, meta] = useField(name);

  const selectedValue = useMemo(
    () => options.find(({ value }) => value === meta.value) ?? '',
    [options, meta.value],
  );

  const handleChange = useCallback(
    ({ value }: Option) => {
      setFieldValue(name, value);
    },
    [setFieldValue, name],
  );

  return (
    <FormikField
      name={name}
      isClearable={false}
      {...props}
      options={options}
      value={selectedValue}
      onChange={handleChange}
      as={StyledSelectMenu}
      $maxVisibleOptions={openMenuMaxVisibleOptions}
    />
  );
}

export default React.memo(FormikFieldSelectMenu);
